import { db } from "@/firebase";
import { collection, doc, getDoc, getDocs, query, where, updateDoc, Timestamp } from "firebase/firestore";
import { logError, logInfo, logWarn } from "@/utils/secureLogger";

/**
 * Check if a user is archived but still exists
 */
export const checkUserIsArchived = async (userId: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    // Check various possible archive status fields
    return userData?.archived === true || 
           userData?.status === 'archived' || 
           userData?.isActive === false ||
           userData?.applicationStatus === 'archived';
  } catch (error) {
    logError("Error checking user archive status", error instanceof Error ? error : new Error(String(error)));
    return false;
  }
};

/**
 * Check if a user exists and is active (not archived) in the users collection
 */
export const checkUserExists = async (userId: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      return false;
    }
    
    // If user exists, check if they're archived
    const userData = userDoc.data();
    // Only consider user as "non-existent" if they're completely deleted
    // Archived users still exist, just inactive
    return true;
  } catch (error) {
    logError("Error checking user existence", error instanceof Error ? error : new Error(String(error)));
    return false;
  }
};

/**
 * Check if multiple users exist in the users collection
 */
export const checkUsersExist = async (userIds: string[]): Promise<{ [userId: string]: boolean }> => {
  const results: { [userId: string]: boolean } = {};
  
  try {
    const promises = userIds.map(async (userId) => {
      const exists = await checkUserExists(userId);
      results[userId] = exists;
    });
    
    await Promise.all(promises);
    return results;
  } catch (error) {
    logError("Error checking multiple users existence", error instanceof Error ? error : new Error(String(error)));
    return results;
  }
};

/**
 * Auto-cancel sessions where tutor or student no longer exists
 */
export const cleanupOrphanedSessions = async (): Promise<{
  cancelledSessions: number;
  errors: string[];
}> => {
  const cancelledSessions = [];
  const errors = [];

  try {
    logInfo("Starting cleanup of orphaned sessions...");

    // Get all pending and confirmed sessions
    const sessionsQuery = query(
      collection(db, "sessions"),
      where("sessionStatus", "in", ["pending", "confirmed", "in-progress"])
    );

    const sessionsSnapshot = await getDocs(sessionsQuery);
    const sessions = sessionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Array<{
      id: string;
      tutorId?: string;
      studentId?: string;
      sessionStatus?: string;
      [key: string]: any;
    }>;

    logInfo(`Found ${sessions.length} active sessions to check`);

    // Extract all unique user IDs
    const userIds = new Set<string>();
    sessions.forEach(session => {
      if (session.tutorId) userIds.add(session.tutorId);
      if (session.studentId) userIds.add(session.studentId);
    });

    // Check which users exist
    const userExistence = await checkUsersExist(Array.from(userIds));

    // Process each session
    for (const session of sessions) {
      try {
        const tutorExists = session.tutorId ? userExistence[session.tutorId] : true;
        const studentExists = session.studentId ? userExistence[session.studentId] : true;

        // If either user doesn't exist, cancel the session
        if (!tutorExists || !studentExists) {
          const missingUsers = [];
          if (!tutorExists) missingUsers.push('tutor');
          if (!studentExists) missingUsers.push('student');

          await updateDoc(doc(db, "sessions", session.id), {
            sessionStatus: "cancelled",
            cancellationReason: `Session auto-cancelled: ${missingUsers.join(' and ')} account(s) no longer exist`,
            cancelledBy: "system",
            cancelledAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          });

          cancelledSessions.push(session.id);
          logWarn(`Auto-cancelled session ${session.id} - missing ${missingUsers.join(' and ')}`);
        }
      } catch (error) {
        const errorMsg = `Failed to process session ${session.id}: ${error instanceof Error ? error.message : String(error)}`;
        errors.push(errorMsg);
        logError("Session cleanup error", error instanceof Error ? error : new Error(String(error)));
      }
    }

    logInfo(`Cleanup completed: ${cancelledSessions.length} sessions cancelled, ${errors.length} errors`);

    return {
      cancelledSessions: cancelledSessions.length,
      errors
    };

  } catch (error) {
    const errorMsg = `Fatal error in session cleanup: ${error instanceof Error ? error.message : String(error)}`;
    errors.push(errorMsg);
    logError("Fatal cleanup error", error instanceof Error ? error : new Error(String(error)));
    
    return {
      cancelledSessions: cancelledSessions.length,
      errors
    };
  }
};

/**
 * Check if a specific session has valid users and auto-cancel if not
 */
export const validateSessionUsers = async (sessionId: string, tutorId?: string, studentId?: string): Promise<boolean> => {
  try {
    const userIds = [tutorId, studentId].filter(Boolean) as string[];
    if (userIds.length === 0) return true;

    const userExistence = await checkUsersExist(userIds);
    
    const tutorExists = tutorId ? userExistence[tutorId] : true;
    const studentExists = studentId ? userExistence[studentId] : true;

    if (!tutorExists || !studentExists) {
      const missingUsers = [];
      if (!tutorExists) missingUsers.push('tutor');
      if (!studentExists) missingUsers.push('student');

      // Auto-cancel the session
      await updateDoc(doc(db, "sessions", sessionId), {
        sessionStatus: "cancelled",
        cancellationReason: `Session auto-cancelled: ${missingUsers.join(' and ')} account(s) no longer exist`,
        cancelledBy: "system",
        cancelledAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      logWarn(`Auto-cancelled session ${sessionId} - missing ${missingUsers.join(' and ')}`);
      return false;
    }

    return true;
  } catch (error) {
    logError("Error validating session users", error instanceof Error ? error : new Error(String(error)));
    return true; // Don't cancel on error, just log it
  }
};

/**
 * Replace user names with "Deactivated User" for display purposes
 */
export const sanitizeUserDisplay = (userName: string | undefined, userId: string, userExists: boolean): string => {
  if (!userExists || !userName) {
    return "Deactivated User";
  }
  return userName;
};